import produce from "immer";
import find from "lodash/find";
import pullAllBy from "lodash/pullAllBy";
import { CustomizationState } from "../";
import { ContentEditablePicture } from "../../../types/customization/content";
import { sizeDmmToMm } from "../../../utils/customization/box";
import {
  calcDefaultCropping,
  calcNextCropping,
  isZoomedOut,
} from "../../../utils/customization/cropping";
import {
  findEditablePictureDefinition,
  findPage,
  findPageDefinition,
} from "../../../utils/customization/finders";
import { findPhoto } from "../../../utils/selection/finders";

export interface SwapEditablePictureElementPictureAction {
  type: "swapEditablePictureElementPicture";
  pageKeyA: string;
  pageKeyB: string;
  editablePictureKeyA: string;
  editablePictureKeyB: string;
}

export function swapEditablePictureElementPicture(
  state: CustomizationState,
  action: SwapEditablePictureElementPictureAction
): CustomizationState {
  return produce(state, (draftState) => {
    const pageA = findPage(action.pageKeyA, draftState.content);
    const pageDefinitionA = findPageDefinition(pageA.pageDefinitionKey, draftState.template);
    const editablePictureDefinitionA = findEditablePictureDefinition(
      action.editablePictureKeyA,
      pageDefinitionA
    );
    const elementASizeMm = sizeDmmToMm(editablePictureDefinitionA);
    const ratioA = elementASizeMm.width.value / elementASizeMm.height.value;
    const editablePictureA = find(
      pageA.editablePictures,
      ({ editablePictureKey }) => editablePictureKey === action.editablePictureKeyA
    );

    const pageB = findPage(action.pageKeyB, draftState.content);
    const pageDefinitionB = findPageDefinition(pageB.pageDefinitionKey, draftState.template);
    const editablePictureDefinitionB = findEditablePictureDefinition(
      action.editablePictureKeyB,
      pageDefinitionB
    );
    const elementBSizeMm = sizeDmmToMm(editablePictureDefinitionB);
    const ratioB = elementBSizeMm.width.value / elementBSizeMm.height.value;
    const editablePictureB = find(
      pageB.editablePictures,
      ({ editablePictureKey }) => editablePictureKey === action.editablePictureKeyB
    );

    pullAllBy(pageA.editablePictures, [editablePictureA], "editablePictureKey");
    pullAllBy(pageB.editablePictures, [editablePictureB], "editablePictureKey");

    const isZoomedOutEnabledOnA = !editablePictureDefinitionA.shapeKey;
    const isZoomedOutEnabledOnB = !editablePictureDefinitionB.shapeKey;
    if (editablePictureA) {
      const shouldResetCropping =
        isZoomedOutEnabledOnA && !isZoomedOutEnabledOnB && isZoomedOut(editablePictureA);
      const selectionPhotoA = findPhoto(editablePictureA.selectionPhotoKey, draftState.selection);

      const newCroppingB = shouldResetCropping
        ? calcDefaultCropping(
            { selectionPhoto: selectionPhotoA, nextRatio: ratioB },
            isZoomedOutEnabledOnB
          )
        : calcNextCropping(
            {
              nextRatio: ratioB,
              prevCropping: editablePictureA.cropping,
              prevRatio: ratioA,
            },
            isZoomedOutEnabledOnB
          );

      const newEditablePictureB: ContentEditablePicture = {
        cropping: newCroppingB,
        editablePictureKey: editablePictureDefinitionB.key,
        filterTag: editablePictureA.filterTag,
        selectionPhotoKey: selectionPhotoA.key,
      };

      pageB.editablePictures.push(newEditablePictureB);
    }

    if (editablePictureB) {
      const shouldResetCropping =
        isZoomedOutEnabledOnB && !isZoomedOutEnabledOnA && isZoomedOut(editablePictureB);
      const selectionPhotoB = findPhoto(editablePictureB.selectionPhotoKey, draftState.selection);

      const newCroppingA = shouldResetCropping
        ? calcDefaultCropping(
            { selectionPhoto: selectionPhotoB, nextRatio: ratioA },
            isZoomedOutEnabledOnB
          )
        : calcNextCropping(
            {
              nextRatio: ratioA,
              prevCropping: editablePictureB.cropping,
              prevRatio: ratioB,
            },
            isZoomedOutEnabledOnB
          );

      const newEditablePictureA: ContentEditablePicture = {
        cropping: newCroppingA,
        editablePictureKey: editablePictureDefinitionA.key,
        filterTag: editablePictureB.filterTag,
        selectionPhotoKey: selectionPhotoB.key,
      };

      pageA.editablePictures.push(newEditablePictureA);
    }
  });
}
